import type { CSSProperties } from 'react';
import { useMemo } from 'react';
import { useDataTable } from '../DataTableProvider';

export interface UseCellPositioningReturn {
  containerStyle: CSSProperties;
  contentStyle: CSSProperties;
}

/**
 * Generate cell positioning styles for absolute layout
 *
 * This hook creates memoized style objects for positioning cells in the virtual
 * scrolling grid. It handles z-index, overflow, and dimensions based on active state.
 *
 * @param positionOffset - The x/y pixel offset for absolute positioning
 * @param active - Whether the cell is in active edit mode
 * @returns Style objects for container and content elements
 */
export const useCellPositioning = (
  positionOffset: { x: number; y: number },
  active = false
): UseCellPositioningReturn => {
  const { dataTableService } = useDataTable();
  const { cellWidth, cellHeight } = dataTableService.dimensions;

  const containerStyle = useMemo<CSSProperties>(
    () => ({
      position: 'absolute',
      top: positionOffset.y,
      left: positionOffset.x,
      zIndex: active ? 1 : 0,
      overflow: active ? 'visible' : 'hidden',
      width: `${cellWidth}px`,
      height: `${cellHeight}px`,
    }),
    [positionOffset.x, positionOffset.y, active, cellWidth, cellHeight]
  );

  const contentStyle = useMemo<CSSProperties>(
    () => ({
      height: active ? 'auto' : '100%',
    }),
    [active]
  );

  return { containerStyle, contentStyle };
};
